import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatTooltipModule} from '@angular/material/tooltip';
import {
  MaporiumAccordionItemComponent
} from '../shared/components/maporium-accordion/maporium-accordion-item.component';
import {CdkAccordion} from '@angular/cdk/accordion';
import {CreditApiService} from '../api/services/credit.service';
import {UserBalanceDetail} from '../api/interfaces/credit.interface';
import {CreditsPurchaseDialogComponent} from './credits-purchase-dialog.component';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {AiStateService} from '../services/ai-state.service';
import {UserService} from '../api/services/user.service';

@Component({
  selector: 'ai-usage-dialog',
  standalone: true,
  imports: [
    CommonModule, MatDialogModule, MatButtonModule, MatIconModule,
    MatProgressSpinnerModule, MatTooltipModule, MaporiumAccordionItemComponent, CdkAccordion,
    TranslatePipe, ReactiveFormsModule, MatSlideToggleModule,
  ],
  styles: [`
    .ai-master-toggle {
      margin-bottom: 8px;
    }
    .credits-box {
      padding: 16px;
      border-radius: 8px;
      text-align: center;
      margin: 16px auto;
    }

    .credits-box.positive {
      border: 2px solid #6aa84f;
      background: #edf5ea;
    }

    .credits-box.negative {
      border: 2px solid #cc0000;
      background: #fae6e6;
    }

    .credits-number {
      font-family: 'Oswald', sans-serif;
      font-weight: 600;
      font-size: 42px;
      line-height: 1.2;
    }

    .credits-number.positive {
      color: #6aa84f;
    }

    .credits-number.negative {
      color: #cc0000;
    }

    .credits-label {
      font-family: 'Oswald', sans-serif;
      font-weight: 600;
      font-size: 28px;
    }

    .credits-label.positive {
      color: #6aa84f;
    }

    .credits-label.negative {
      color: #cc0000;
    }

    .account-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px 8px;
      padding: 6px 0;
    }

    .row-icon {
      width: 30px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      color: #555;
    }

    .row-info {
      flex-shrink: 1;
      font-size: 13px;
      color: #666;
      min-width: 0;
    }

    .row-actions {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      gap: 6px;
      margin-left: auto;
    }

    .confirm-btn {
      background-color: #3D85C6;
      color: white;
      border: none;
      padding: 0 16px;
      font-family: 'Oswald', sans-serif;
      font-size: 15px;
      text-transform: uppercase;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      align-items: center;
      height: 29px;
      box-sizing: border-box;
    }

    .confirm-btn:hover {
      background-color: #2a6ba6;
    }

    .explanation {
      font-size: 13px;
      color: #666;
      padding: 8px 0;
      text-align: center;
    }

    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 32px;
    }
  `],
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0">
        <span class="icon-smap-credit-usage me-2"></span>{{ 'AI_USAGE.TITLE' | translate }}
      </h2>
      <span class="icon-smap-action-close close-button" (click)="close()" [matTooltip]="'EMBED_DIALOG.TOOLTIP_CLOSE_PANEL' | translate"></span>
    </div>

    <mat-dialog-content>
      <div class="account-row ai-master-toggle">
        <div class="row-icon">
          <i class="icon-smap-provider-any"></i>
        </div>
        <div class="row-info">
          {{ 'AI_USAGE.ENABLE_AI_FEATURES' | translate }}
        </div>
        <div class="row-actions">
          <mat-slide-toggle [formControl]="aiEnabledControl"></mat-slide-toggle>
        </div>
      </div>
      @if (loading) {
        <div class="loading-container">
          <mat-spinner diameter="40"></mat-spinner>
        </div>
      } @else if (balance) {
        <cdk-accordion>
          <maporium-accordion-item [title]="'AI_USAGE.CREDITS_BALANCE' | translate">
            <div class="credits-box" [class.positive]="isPositive" [class.negative]="!isPositive">
              <div class="credits-number" [class.positive]="isPositive" [class.negative]="!isPositive">
                {{ absoluteBalance | number }}
              </div>
              <div class="credits-label" [class.positive]="isPositive" [class.negative]="!isPositive">
                {{ (qualifier === 'remaining' ? 'AI_ASSISTANT.REMAINING_CREDITS' : 'AI_ASSISTANT.OVERDRAWN_CREDITS') | translate }}
              </div>
            </div>

            <div class="account-row">
              <div class="row-icon">
                @if (balance.planIcon) {
                  <i [ngClass]="balance.planIcon"></i>
                } @else {
                  <mat-icon>redeem</mat-icon>
                }
              </div>
              <div class="row-info">
                {{ 'AI_USAGE.FREE_CREDITS_INFO' | translate: { amount: formatCredits(balance.freeCredits), date: replenishmentDate, max: formatCredits(balance.freeCreditsAllocation) } }}
              </div>
            </div>

            <div class="account-row">
              <div class="row-icon">
                <i class="icon-smap-credit-purchase"></i>
              </div>
              <div class="row-info">
                {{ 'AI_USAGE.PAID_CREDITS_INFO' | translate: { amount: formatCredits(balance.paidCredits) } }}
              </div>
              <div class="row-actions">
                <button class="confirm-btn" (click)="openPurchaseDialog()">{{ 'AI_USAGE.ADD_MORE' | translate }}</button>
              </div>
            </div>

            <div class="explanation">
              {{ 'AI_USAGE.FREE_CREDITS_EXPLANATION' | translate }}
            </div>
          </maporium-accordion-item>
        </cdk-accordion>
      }
    </mat-dialog-content>
  `,
})
export class AiUsageDialogComponent implements OnInit {
  balance: UserBalanceDetail | null = null;
  loading = true;

  /** MAP-147: AI is optional; this is the user's own switch for all of it. */
  aiEnabledControl = new FormControl<boolean>(true, {nonNullable: true});

  constructor(
    private dialogRef: MatDialogRef<AiUsageDialogComponent>,
    private dialog: MatDialog,
    private creditApiService: CreditApiService,
    private translate: TranslateService,
    private aiStateService: AiStateService,
    private userService: UserService,
  ) {
  }

  ngOnInit() {
    this.loadBalance();
    this.aiEnabledControl.setValue(this.aiStateService.isAiVisible(), {emitEvent: false});
    this.aiEnabledControl.valueChanges.subscribe((enabled) => {
      this.aiStateService.setAiFeaturesVisible(enabled);
      this.userService.updateAiSettings({aiFeaturesEnabled: enabled}).subscribe();
    });
  }

  loadBalance() {
    this.loading = true;
    this.creditApiService.getBalance().subscribe({
      next: (balance) => {
        this.balance = balance;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  get isPositive(): boolean {
    return (this.balance?.currentBalance ?? 0) > 0;
  }

  get absoluteBalance(): number {
    return Math.abs(Math.round(this.balance?.currentBalance ?? 0));
  }

  get qualifier(): string {
    return (this.balance?.currentBalance ?? 0) >= 0 ? 'remaining' : 'overdrawn';
  }

  get replenishmentDate(): string {
    if (!this.balance?.replenishmentDate) return this.translate.instant('AI_USAGE.NOT_AVAILABLE');
    return new Date(this.balance.replenishmentDate).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  }

  formatCredits(n: number): string {
    return Math.round(n).toLocaleString();
  }

  openPurchaseDialog() {
    const ref = this.dialog.open(CreditsPurchaseDialogComponent, {
      width: '780px',
      autoFocus: false,
      data: {},
    });
    ref.afterClosed().subscribe((updatedBalance: UserBalanceDetail | null) => {
      if (updatedBalance) {
        this.balance = updatedBalance;
      }
    });
  }

  close() {
    this.dialogRef.close();
  }
}
